import { ProfilePermissionType } from '@/api/backend/types';
import { PROFILE_PERMISSION } from '@/lib/constants';
import { redirect } from '@tanstack/react-router';

type RouteGuardContext = {
  authStore: {
    getState: () => {
      activeProfile: {
        permissions?: ProfilePermissionType[];
      };
    };
  };
};

/**
 * Helper function to wait for profile to load and check permissions
 *
 * @param context Router context containing authStore
 * @param checkPermission Function that checks if user has required permissions
 * @param permissionType Type of permission being checked (for logging)
 */
const checkPermission = async (
  context: RouteGuardContext,
  checkPermission: (permissions: ProfilePermissionType[]) => boolean,
  permissionType: ProfilePermissionType,
) => {
  console.debug(`[GUARD] Checking ${permissionType} permissions...`);

  // Wait for activeProfile to be loaded
  let activeProfile;
  while (!(activeProfile = context.authStore.getState().activeProfile)?.permissions) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  // At this point we know permissions exist
  const permissions = activeProfile.permissions as ProfilePermissionType[];
  const hasPermission = checkPermission(permissions);

  if (!hasPermission) {
    console.debug(`[GUARD] User does not have ${permissionType} permissions.`);

    throw redirect({
      to: '/dashboard',
    });
  }

  console.debug(`[GUARD] User has ${permissionType} permissions.`);
};

/**
 * Checks if the user is authenticated
 * @param context Router context containing authStore
 * @returns void if authenticated, redirects to login if not
 */

/**
 * Checks if the user has the required permissions to view contract details
 * @param context Router context containing authStore
 * @returns void if user has permission, redirects to dashboard if not
 */
export const requireContractAccess = (context: RouteGuardContext) => {
  return checkPermission(
    context,
    (permissions) => permissions.includes(PROFILE_PERMISSION.PMS_CONTRACT),
    PROFILE_PERMISSION.PMS_CONTRACT,
  );
};

/**
 * Checks if the user has the required permissions to view permit details
 * @param context Router context containing authStore
 * @returns void if user has permission, redirects to dashboard if not
 */
export const requirePermitAccess = (context: RouteGuardContext) => {
  return checkPermission(
    context,
    (permissions) => permissions.includes(PROFILE_PERMISSION.PMS_PERMIT),
    PROFILE_PERMISSION.PMS_PERMIT,
  );
};

/**
 * Checks if the user has the required permissions to perform payment operations
 * @param context Router context containing authStore
 * @returns void if user has permission, redirects to dashboard if not
 */
export const requirePaymentAccess = (context: RouteGuardContext) => {
  return checkPermission(
    context,
    (permissions) => permissions.includes(PROFILE_PERMISSION.PMS_PAY),
    PROFILE_PERMISSION.PMS_PAY,
  );
};

/**
 * Checks if the user has any of the provided permissions
 * @param context Router context containing authStore
 * @param permissions Array of permissions to check
 * @returns void if user has any permission, redirects to dashboard if not
 */
export const requireAnyPermission = (context: RouteGuardContext, permissions: ProfilePermissionType[]) => {
  return checkPermission(
    context,
    (userPermissions) => permissions.some((permission) => userPermissions.includes(permission)),
    permissions[0], // Use first permission for logging
  );
};
